import { Component } from '@components/Component/Component.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { clamp, createElement, getID, isMobile, isOverflowingEllipsis } from '@components/Utility/index.js';
import { map } from 'rxjs';
import { eventContextGet } from '@components/EventConfigManager/EventConfigManagerUtil.js';
export class TooltipManager extends Component {
    constructor() {
        super(...arguments);
        this.componentName = 'TooltipManager';
        this.rootElementType = 'div';
        this.tooltipComponent = undefined;
        this.callbacks = new Map();
        this.pendingTarget = null;
        this.showSeq = 0; // increments to cancel stale delayed shows
        this.handleMouseOver = (event) => {
            let target = event.target;
            if (!target)
                return;
            if (target && !target.hasAttribute('data-tooltip'))
                target = target.closest('[data-tooltip]');
            if (!target) {
                this.clickAwaySuppressedAnchor = undefined;
                return;
            }
            if (this.clickAwaySuppressedAnchor && this.clickAwaySuppressedAnchor === target) {
                return;
            }
            this.clickAwaySuppressedAnchor = undefined;
            this.lastMouse = { x: event.clientX, y: event.clientY };
            // schedule show after delay, and only THEN compute content so we dont spend processing on computing overflows etc unless the tooltip is actually going to show
            this.scheduleTooltipShow(target, event);
        };
    }
    draw() {
        super.draw();
        // TODO: maybe make this a tooltip option, just was a quick add on for label for now.
        // this.registerTemplate('overflow', (context: EventContext) => {
        // 	const el = context.event!.target as HTMLElement;
        // 	const isOverflowing = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
        // 	return isOverflowing ? el.innerHTML : '';
        // });
        this.registerTemplate('overflow-ellipsis', (context) => {
            const el = context.event.target;
            const isOverflowing = isOverflowingEllipsis(el);
            return isOverflowing ? el.innerHTML : '';
        });
        // tooltips seem detrimental to mobile UX, if we add them not sure how it should really work.
        if (!isMobile()) {
            this.registerEvent('mouseover', document, this.handleMouseOver.bind(this));
        }
    }
    registerTemplate(id, fn) {
        this.callbacks.set(id, fn);
    }
    unregisterTemplate(id) {
        this.callbacks.delete(id);
    }
    registerTooltip(element, options = {}) {
        let callbackId = '';
        if (!options.tooltip && !options.captionKey && !options.callback && !options.templateId) {
            this.unregisterTooltip(element);
            return '';
        }
        element.dataset.tooltip = options.tooltip || '';
        if (options.captionKey) {
            element.dataset.tooltipCaptionKey = options.captionKey;
        }
        if (options.delay) {
            element.dataset.tooltipDelay = options.delay.toString();
        }
        if (options.relativeToMouse) {
            element.dataset.tooltipRelativeToMouse = '1';
        }
        if (typeof options.maxDistanceFromMouse === 'number') {
            element.dataset.tooltipMaxDistance = String(options.maxDistanceFromMouse);
        }
        if (typeof options.gap === 'number') {
            element.dataset.tooltipGap = String(options.gap);
        }
        const prio = this.parseSidePriority(options.sidePriority);
        if (prio?.length) {
            element.dataset.tooltipSidePriority = prio.join(',');
        }
        if (options.clickAway) {
            element.dataset.tooltipClickAway = '1';
        }
        else {
            delete element.dataset.tooltipClickAway;
        }
        if (options.callback) {
            callbackId = options.callbackId || getID('tooltip');
            element.dataset.tooltipCallbackId = callbackId;
            this.callbacks.set(callbackId, options.callback);
        }
        if (options.templateId) {
            element.dataset.tooltipTemplateId = options.templateId;
        }
        return callbackId;
    }
    unregisterTooltip(element, callbackId) {
        callbackId = callbackId || element?.dataset.tooltipCallbackId;
        if (callbackId) {
            this.callbacks.delete(callbackId);
        }
        if (element) {
            delete element.dataset.tooltipTemplateId;
            delete element.dataset.tooltipCallbackId;
            delete element.dataset.tooltip;
            delete element.dataset.tooltipCaptionKey;
            delete element.dataset.tooltipDelay;
            delete element.dataset.tooltipRelativeToMouse;
            delete element.dataset.tooltipMaxDistance;
            delete element.dataset.tooltipSidePriority;
            delete element.dataset.tooltipClickAway;
        }
    }
    scheduleTooltipShow(forElement, event) {
        // cancel any pending show for a previous element
        if (this.hoverTimer) {
            clearTimeout(this.hoverTimer);
            this.hoverTimer = undefined;
        }
        if (this.pendingTarget !== forElement) {
            this.removeTooltip();
        }
        this.pendingTarget = forElement;
        let delay = 0;
        if (forElement.dataset.tooltipDelay) {
            const n = Number(forElement.dataset.tooltipDelay);
            if (!Number.isNaN(n) && n >= 0)
                delay = n;
        }
        const seq = ++this.showSeq; // capture sequence to detect staleness
        const fire = () => {
            // if user moved off or another hover started, abort
            if (this.pendingTarget !== forElement || seq !== this.showSeq)
                return;
            // re-validate attribute still present
            if (!forElement.hasAttribute('data-tooltip'))
                return;
            // Only now compute the content (callback/overflow work happens here)
            const tooltipContent = this.tooltipFromElementGet(forElement, event);
            if (!tooltipContent)
                return;
            this.createTooltip(tooltipContent, forElement);
            if (!this.tooltipComponent)
                return;
            this.positionTooltip(forElement, this.tooltipComponent, this.lastMouse);
            // Watch for target removal from DOM
            this.mutationObserver?.disconnect();
            this.mutationObserver = new MutationObserver((mutations) => {
                let isTargetRemoved = false;
                mutations.forEach((mutation) => {
                    mutation.removedNodes.forEach((node) => {
                        if (node.contains && node.contains(forElement)) {
                            isTargetRemoved = true;
                        }
                    });
                });
                if (isTargetRemoved)
                    this.removeTooltip();
            });
            this.mutationObserver.observe(document.body, { childList: true, subtree: true });
        };
        if (delay > 0) {
            this.hoverTimer = window.setTimeout(() => {
                this.hoverTimer = undefined;
                fire();
            }, delay);
        }
        else {
            fire();
        }
        if (this.mouseLeaveEventId) {
            this.unRegisterEvent(this.mouseLeaveEventId);
            this.mouseLeaveEventId = undefined;
        }
        this.mouseLeaveEventId = this.registerEvent('mouseleave', forElement, this.checkRemoveTooltip.bind(this));
        if (forElement.dataset.tooltipClickAway === '1' || forElement.dataset.tooltipClickAway === 'true') {
            if (this.clickAwayEventId) {
                this.unRegisterEvent(this.clickAwayEventId);
                this.clickAwayEventId = undefined;
            }
            this.clickAwayEventId = this.registerEvent('click', forElement, this.handleClickAway.bind(this));
        }
        else if (this.clickAwayEventId) {
            // If previous element had clickAway but this one doesn't, clean up
            this.unRegisterEvent(this.clickAwayEventId);
            this.clickAwayEventId = undefined;
        }
    }
    handleClickAway(event) {
        const anchor = event.currentTarget;
        this.clickAwaySuppressedAnchor = anchor;
        if (this.pendingTarget === anchor || this.tooltipComponent) {
            this.removeTooltip();
        }
    }
    tooltipFromElementGet(element, event) {
        let tooltipContent = element.getAttribute('data-tooltip') || '';
        if (element.dataset.tooltipCaptionKey) {
            return this.CaptionManager.onLoad$(element.dataset.tooltipCaptionKey, true).pipe(map((caption) => caption.tooltip || caption.caption));
        }
        else if (element.dataset.tooltipCallbackId || element.dataset.tooltipTemplateId) {
            const component = DIContainer.getInstance('ComponentRegistry').getComponentByElement(element);
            const context = eventContextGet({ initiator: component, event });
            const callbackId = element.dataset.tooltipCallbackId || element.dataset.tooltipTemplateId;
            tooltipContent = this.callbacks.get(callbackId)(context);
        }
        return tooltipContent;
    }
    createTooltip(content, forElement) {
        if (this.tooltipComponent)
            return;
        const tooltip = DIContainer.new('Tooltip', '', createElement('div'));
        if (typeof content === 'string') {
            tooltip.value = content;
            tooltip.draw();
        }
        else {
            tooltip.draw();
            tooltip.handleProgress(0);
            this.asyncTooltipSubscription = content.subscribe((content) => {
                tooltip.handleProgress(100);
                tooltip.value = content;
                this.positionTooltip(forElement, tooltip, this.lastMouse);
            });
        }
        this.rootElement.appendChild(tooltip.rootElement);
        this.tooltipComponent = tooltip;
    }
    readPositionOptions(from) {
        const relativeToMouse = from.dataset.tooltipRelativeToMouse === '1' || from.dataset.tooltipRelativeToMouse === 'true';
        const gap = from.dataset.tooltipGap ? Number(from.dataset.tooltipGap) : 8;
        const maxDistanceFromMouse = from.dataset.tooltipMaxDistance ? Number(from.dataset.tooltipMaxDistance) : undefined;
        const sidePriority = from.dataset.tooltipSidePriority
            ? from.dataset.tooltipSidePriority.split(',').filter(Boolean)
            : undefined;
        return { relativeToMouse, maxDistanceFromMouse, sidePriority, gap };
    }
    positionTooltip(anchor, toolTip, mouse) {
        if (!toolTip?.rootElement || !anchor)
            return;
        const el = toolTip.rootElement;
        const st = el.style;
        const { relativeToMouse, maxDistanceFromMouse, sidePriority, gap } = this.readPositionOptions(anchor);
        // constants
        // const gap = 8; // space between anchor/mouse and tip
        const viewportPad = 8; // min distance to screen edges
        const maxWidthCap = Math.min(400, window.innerWidth - viewportPad * 2);
        st.maxWidth = `${maxWidthCap}px`;
        // Move off-screen to measure natural size
        st.visibility = 'hidden';
        st.left = `-10000px`;
        st.top = `-10000px`;
        const targetRect = anchor.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        // Helper to compute space by edges (prevents center-based overestimates)
        const spaceByEdges = {
            top: targetRect.top - viewportPad,
            bottom: vh - targetRect.bottom - viewportPad,
            left: targetRect.left - viewportPad,
            right: vw - targetRect.right - viewportPad,
        };
        const spaceByPoint = (pt) => ({
            top: pt.y - viewportPad,
            bottom: vh - pt.y - viewportPad,
            left: pt.x - viewportPad,
            right: vw - pt.x - viewportPad,
        });
        const space = (relativeToMouse && mouse) ? spaceByPoint(mouse) : spaceByEdges;
        const tryOrder = (() => {
            if (sidePriority?.length)
                return sidePriority;
            // Fallback heuristic using true edge-space
            const canSide = (s) => space[s] >= 120; // some reasonable minimum
            if (canSide('left') || canSide('right'))
                return space.right >= space.left ? ['right', 'left', 'bottom', 'top'] : ['left', 'right', 'bottom', 'top'];
            return space.bottom >= space.top ? ['bottom', 'top', 'right', 'left'] : ['top', 'bottom', 'right', 'left'];
        })();
        // Try placements in order; pick the first that doesn't overlap the anchor after clamping
        const choosePlacement = () => {
            for (const placement of tryOrder) {
                // candidate max-height by placement
                let maxH = 500;
                if (placement === 'top')
                    maxH = Math.min(500, space.top - gap);
                if (placement === 'bottom')
                    maxH = Math.min(500, space.bottom - gap);
                if (placement === 'left' || placement === 'right')
                    maxH = Math.min(500, vh - viewportPad * 2);
                maxH = Math.max(120, maxH);
                // measure with this max height
                st.maxHeight = `${maxH}px`;
                const w = Math.min(el.offsetWidth, maxWidthCap);
                const h = Math.min(el.offsetHeight, maxH);
                let left = 0, top = 0;
                if (relativeToMouse && mouse) {
                    switch (placement) {
                        case 'right':
                            left = Math.min(mouse.x + gap, vw - w - viewportPad);
                            top = clamp(mouse.y - h / 2, viewportPad, vh - h - viewportPad);
                            break;
                        case 'left':
                            left = Math.max(mouse.x - w - gap, viewportPad);
                            top = clamp(mouse.y - h / 2, viewportPad, vh - h - viewportPad);
                            break;
                        case 'bottom':
                            top = Math.min(mouse.y + gap, vh - h - viewportPad);
                            left = clamp(mouse.x - w / 2, viewportPad, vw - w - viewportPad);
                            break;
                        case 'top':
                            top = Math.max(mouse.y - h - gap, viewportPad);
                            left = clamp(mouse.x - w / 2, viewportPad, vw - w - viewportPad);
                            break;
                    }
                }
                else {
                    switch (placement) {
                        case 'right':
                            left = Math.min(targetRect.right + gap, vw - w - viewportPad);
                            top = clamp(targetRect.top + (targetRect.height - h) / 2, viewportPad, vh - h - viewportPad);
                            break;
                        case 'left':
                            left = Math.max(targetRect.left - w - gap, viewportPad);
                            top = clamp(targetRect.top + (targetRect.height - h) / 2, viewportPad, vh - h - viewportPad);
                            break;
                        case 'bottom':
                            top = Math.min(targetRect.bottom + gap, vh - h - viewportPad);
                            left = clamp(targetRect.left + (targetRect.width - w) / 2, viewportPad, vw - w - viewportPad);
                            break;
                        case 'top':
                            top = Math.max(targetRect.top - h - gap, viewportPad);
                            left = clamp(targetRect.left + (targetRect.width - w) / 2, viewportPad, vw - w - viewportPad);
                            break;
                    }
                    // Max distance from mouse adjustment (primary axis), only when not relativeToMouse
                    if (typeof maxDistanceFromMouse === 'number' && this.lastMouse) {
                        // TODO: this doesnt work perfectly on some stuff like tall Resource Console events 
                        if (placement === 'top' || placement === 'bottom') {
                            const centerX = left + w / 2;
                            const minX = this.lastMouse.x - maxDistanceFromMouse;
                            const maxX = this.lastMouse.x + maxDistanceFromMouse;
                            let newCenterX = clamp(centerX, minX, maxX);
                            newCenterX = clamp(newCenterX, viewportPad + w / 2, vw - viewportPad - w / 2);
                            left = Math.round(newCenterX - w / 2);
                        }
                        else {
                            const centerY = top + h / 2;
                            const minY = this.lastMouse.y - maxDistanceFromMouse;
                            const maxY = this.lastMouse.y + maxDistanceFromMouse;
                            let newCenterY = clamp(centerY, minY, maxY);
                            newCenterY = clamp(newCenterY, viewportPad + h / 2, vh - viewportPad - h / 2);
                            top = Math.round(newCenterY - h / 2);
                        }
                    }
                }
                // // Validate: ensure we didn't overlap the anchor due to viewport clamping
                // let valid = true;
                // if (placement === 'bottom') valid = top >= targetRect.bottom + gap - 0.5; // allow tiny epsilon
                // if (placement === 'top') valid = (top + h) <= targetRect.top - gap + 0.5;
                // if (placement === 'right') valid = left >= targetRect.right + gap - 0.5;
                // if (placement === 'left') valid = (left + w) <= targetRect.left - gap + 0.5;
                //
                // if (valid) return { placement, left, top, w, h, maxH: maxH };
                // Validate: ensure we didn't overlap the anchor due to viewport clamping
                let valid = true;
                if (relativeToMouse && mouse) {
                    const mx = mouse.x;
                    const my = mouse.y;
                    // For mouse-relative placements, ensure tip stays on the intended side of the mouse + gap
                    switch (placement) {
                        case 'bottom':
                            valid = top >= (my + gap - 0.5);
                            break;
                        case 'top':
                            valid = (top + h) <= (my - gap + 0.5);
                            break;
                        case 'right':
                            valid = left >= (mx + gap - 0.5);
                            break;
                        case 'left':
                            valid = (left + w) <= (mx - gap + 0.5);
                            break;
                    }
                }
                else {
                    // Original element-anchored validation
                    if (placement === 'bottom')
                        valid = top >= targetRect.bottom + gap - 0.5;
                    if (placement === 'top')
                        valid = (top + h) <= targetRect.top - gap + 0.5;
                    if (placement === 'right')
                        valid = left >= targetRect.right + gap - 0.5;
                    if (placement === 'left')
                        valid = (left + w) <= targetRect.left - gap + 0.5;
                }
                if (valid) {
                    // Extra safety: tiny post-clamp nudge away from the anchor
                    if (relativeToMouse && mouse) {
                        const mx = mouse.x, my = mouse.y;
                        const EPS = 0.001;
                        if (placement === 'bottom' && top < my + gap) {
                            top = Math.min(my + gap, vh - h - viewportPad);
                        }
                        else if (placement === 'top' && (top + h) > my - gap) {
                            top = Math.max(my - gap - h, viewportPad);
                        }
                        else if (placement === 'right' && left < mx + gap) {
                            left = Math.min(mx + gap, vw - w - viewportPad);
                        }
                        else if (placement === 'left' && (left + w) > mx - gap) {
                            left = Math.max(mx - gap - w, viewportPad);
                        }
                        // Re-check after nudge (in case we hit viewport clamps)
                        if (placement === 'bottom')
                            valid = top >= my + gap - EPS;
                        if (placement === 'top')
                            valid = (top + h) <= my - gap + EPS;
                        if (placement === 'right')
                            valid = left >= mx + gap - EPS;
                        if (placement === 'left')
                            valid = (left + w) <= mx - gap + EPS;
                    }
                    if (valid)
                        return { placement, left, top, w, h, maxH };
                }
            }
            // Fallback: use first in order even if overlapping (rare). Use conservative max height.
            const fallback = tryOrder[0];
            st.maxHeight = `${Math.max(120, Math.min(500, vh - viewportPad * 2))}px`;
            const w = Math.min(el.offsetWidth, maxWidthCap);
            const h = Math.min(el.offsetHeight, Math.max(120, Math.min(500, vh - viewportPad * 2)));
            let left = clamp(targetRect.left + (targetRect.width - w) / 2, viewportPad, vw - w - viewportPad);
            let top = Math.max(targetRect.top - h - gap, viewportPad);
            if (fallback === 'bottom')
                top = Math.min(targetRect.bottom + gap, vh - h - viewportPad);
            if (fallback === 'left') {
                left = Math.max(targetRect.left - w - gap, viewportPad);
                top = clamp(targetRect.top + (targetRect.height - h) / 2, viewportPad, vh - h - viewportPad);
            }
            if (fallback === 'right') {
                left = Math.min(targetRect.right + gap, vw - w - viewportPad);
                top = clamp(targetRect.top + (targetRect.height - h) / 2, viewportPad, vh - h - viewportPad);
            }
            return { placement: fallback, left, top, w, h, maxH: parseFloat(st.maxHeight) };
        };
        const chosen = choosePlacement();
        // Apply & reveal
        st.maxHeight = `${chosen.maxH}px`;
        st.left = `${chosen.left}px`;
        st.top = `${chosen.top}px`;
        st.visibility = '';
    }
    checkRemoveTooltip(event) {
        const relatedTarget = event.relatedTarget;
        const tooltipElement = this.tooltipComponent?.rootElement;
        // Check if the related target is the tooltip itself or a child of the tooltip
        if (tooltipElement && (relatedTarget === tooltipElement || tooltipElement.contains(relatedTarget))) {
            // Do nothing if moving into the tooltip
            this.registerEvent('mouseleave', tooltipElement, this.removeTooltip.bind(this)); // Listen for when the user leaves the tooltip
        }
        else {
            this.clickAwaySuppressedAnchor = undefined;
            this.removeTooltip();
        }
    }
    removeTooltip() {
        // cancel any delayed show
        if (this.hoverTimer) {
            clearTimeout(this.hoverTimer);
            this.hoverTimer = undefined;
        }
        this.pendingTarget = null;
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = undefined;
        }
        if (this.asyncTooltipSubscription) {
            this.asyncTooltipSubscription.unsubscribe();
            this.asyncTooltipSubscription = undefined;
        }
        if (this.mouseLeaveEventId) {
            this.unRegisterEvent(this.mouseLeaveEventId);
            this.mouseLeaveEventId = undefined;
        }
        if (this.clickAwayEventId) {
            this.unRegisterEvent(this.clickAwayEventId);
            this.clickAwayEventId = undefined;
        }
        if (this.tooltipComponent) {
            this.rootElement.removeChild(this.tooltipComponent.rootElement);
            this.tooltipComponent.destroy();
            this.tooltipComponent = undefined;
        }
    }
    parseSidePriority(input) {
        if (!input)
            return undefined;
        if (Array.isArray(input))
            return input;
        const map = { N: 'top', S: 'bottom', W: 'left', E: 'right' };
        const arr = [];
        for (const ch of input.toUpperCase()) {
            const p = map[ch];
            if (p && !arr.includes(p))
                arr.push(p);
        }
        return arr.length ? arr : undefined;
    }
    template() {
        return '';
    }
    destroy() {
        this.removeTooltip();
        super.destroy();
    }
}
TooltipManager.VERSION = '$Revision: 111977 $';
